import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import {
  from,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { Store } from '@ngxs/store';
import {
  ConsumerEntityService,
  ConsumerEntityState,
} from '../../consumer/services/consumer.entity.service';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  normalizeManyRelations,
  normalizeOneRelation,
} from '../../core/decorators/relation.decorator';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { map } from 'rxjs/operators';
import { MatDivider } from '@angular/material/divider';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import { ModuleEntity } from '../module.entity';
import { StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { SchoolClassEntity } from '../../core/global-enums/school-class/school-class.entity';
import { SchoolClassEntityService } from '../../core/global-enums/school-class/services/school-class.entity.service';
import { EntityStateModel } from '../../core/state/entity.state';
import { MatIcon } from '@angular/material/icon';
import { AuthState } from '../../core/authentication/auth.state';
import { EditorRole } from '../../editor/editor.role.enum';
import { MediaType } from '../../core/types';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { environment } from '../../../environments/environment';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { ObjectConsumerMetadataComponent } from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { TranslocoModule } from '@jsverse/transloco';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { AssetEntity } from '../../asset/asset.entity';
import {
  RelationTableSelectComponent
} from '../../utils/component/relation-table/relation-table-select.component';
import { LearningSectionEntityService } from '../../learning-section/services/learning-section.entity.service';
import { getLearningUnitColumns, getLoadLearningUnitsFn } from '../../learning-unit/learning-unit-relation-table.config';

@Component({
  selector: 'pixl-form-dialog-module',
  templateUrl: './form-dialog-module.component.html',
  imports: [
    CommonModule,
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    MatSelect,
    AsyncPipe,
    MatOption,
    FormsModule,
    MatDivider,
    StateFilterCBComponent,
    MatIcon,
    MatIconButton,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent,
    TranslocoModule,
    PixlEditorComponent,
    RelationTableSelectComponent,
  ],
})
export class FormDialogModuleComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<FormDialogModuleComponent>>(MatDialogRef);

  data? = inject<Partial<ModuleEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  schoolClasses$!: Observable<SchoolClassEntity[]>;
  selectedConsumerName$!: Observable<string>;

  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private learningSectionService = inject(LearningSectionEntityService);
  private schoolClassService = inject(SchoolClassEntityService);

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;

  public editor$ = this.store.select(AuthState.getEditor);
  public auth$ = this.store.select(AuthState.getToken);
  public isZavAdmin = false;

  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';
  public descriptionHtml = '';

  public imageUrl = signal('');

  status = '';

  // for relation table select
  learningUnitColumns = getLearningUnitColumns(this.store, this.learningSectionService);
  loadLearningUnitsFn = getLoadLearningUnitsFn(this.store, this.learningUnitService);

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      preTitle: [data?.preTitle || ''],
      title: [data?.title || ''],
      description: [data?.description || ''],
      status: [data?.status || 'Edit'],
      preview: [data?.preview || []],
      content: this.fb.array(
        (data?.content || []).map((c) => this.fb.control(c)),
      ),
      consumer: [normalizeOneRelation<ConsumerEntity>(data?.consumer)],
      subjects: [normalizeManyRelations(data?.subjects)],
      learningUnits: [normalizeManyRelations(data?.learningUnits)],
      schoolClasses: [normalizeManyRelations(data?.schoolClasses)],
      consumerData: this.fb.group(
        Object.fromEntries(
          Object.entries(data?.consumerData || {}).map(([key, value]) => [
            key,
            Array.isArray(value) ? [value] : value,
          ]),
        ),
      ),
    });
    this.buildURLs();
  }

  ngOnInit() {
    const learningUnitQuery = {
      'page[size]': this.pageSizeLearningUnits,
      'page[number]': this.currentPageLearningUnits,
      'sort': 'title',
    };

    this.learningUnits$ = from(
      this.learningUnitService.ensureLoaded(learningUnitQuery),
    ).pipe(
      switchMap(() =>
        this.store
          .select(this.learningUnitService.allByQuery(learningUnitQuery))
          .pipe(
            map((paginated) => {
              return [...this.selectedLearningUnits, ...paginated].filter(
                (lu, index, self) =>
                  index === self.findIndex((s) => s.id === lu.id),
              );
            }),
          ),
      ),
    );

    const selectedLearningUnitIds = normalizeManyRelations(
      this.data?.learningUnits,
    ) as string[];

    const state = this.store.selectSnapshot(
      this.learningUnitService.stateClass,
    ) as EntityStateModel<LearningUnitEntity>;
    this.selectedLearningUnits = selectedLearningUnitIds
      .map((id) => state.entities[id])
      .filter((e): e is LearningUnitEntity => !!e);

    this.learningUnits$ = this.store
      .select(this.learningUnitService.allByQuery(learningUnitQuery))
      .pipe(
        map((paginated) => {
          return [...this.selectedLearningUnits, ...paginated].filter(
            (seq, index, self) =>
              index === self.findIndex((s) => s.id === seq.id),
          );
        }),
      );

    this.form
      .get('learningUnits')
      ?.setValue(this.selectedLearningUnits.map((s) => s.id));

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery)),
      ),
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery)),
      ),
    );

    const schoolClassQuery = { sort: 'grade' };
    this.schoolClasses$ = from(
      this.schoolClassService.ensureLoaded(schoolClassQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.schoolClassService.allByQuery(schoolClassQuery)),
      ),
    );

    this.auth$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((token) => {
      this.authToken = token;
    });

    this.descriptionHtml = this.data?.description || '';

    this.editor$.subscribe((editor) => {
      if (editor && (editor.role === EditorRole.ZAVAdmin || editor.role === EditorRole.AppAdmin)) {
        this.isZavAdmin = true;
      }
    });

    this.selectedConsumerName$ = this.form.get('consumer')!.valueChanges.pipe(
      startWith(this.form.get('consumer')!.value),
      switchMap((val) => {
        const id = Array.isArray(val) ? val[0] : val;
        if (!id) return of('Metadaten');
        return this.store.select(ConsumerEntityState.byId).pipe(
          map((fn) => fn(id)),
          map((consumer) => consumer?.name || 'Metadaten'),
        );
      }),
    );
  }

  onLearningUnitsChanged(selected: LearningUnitEntity[]): void {
    this.selectedLearningUnits = selected;
    this.form
      .get('learningUnits')
      ?.setValue(selected.map((lu) => lu.id));
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: false,
      subjectId: '',
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetEntity) => {
        if (!answer) return;

        const previewControl = this.form.get('preview');

        if (!previewControl?.value || Array.isArray(previewControl.value)) {
          // initialize as object if empty
          previewControl?.setValue({
            id: '',
            name: '',
            mediaType: '',
            media_url: '',
          });
        }

        // update the form control value
        previewControl?.patchValue({
          id: answer.id,
          mediaType: 'Image',
          media_url: answer.media_url,
          name: answer.name,
        });

        this.cdr.detectChanges();
        this.buildURLs();
      });
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ?
      '/' :
      'https://zav-stage.westermann.de/';

    const preview = this.form.get('preview')?.value;

    if (preview && preview.media_url) {
      this.imageUrl.set(baseUrl + preview.media_url);
    }
  }

  public deleteMediaEntry(): void {
    const previewControl = this.form.get('preview');
    const preview = previewControl?.value;

    const id = preview?.id;
    const name = preview?.name;
    const headerText = 'Bild-Datei löschen';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        id,
        name,
        itemType: 'Datei',
        headerText,
        deleteFn: () => {
          // clear the form control instead of this.data.preview
          previewControl?.setValue({
            id: '',
            name: '',
            mediaType: '',
            media_url: '',
          });
          this.cdr.detectChanges();
          dialogRef.close();
        },
      },
    });
  }

  public getImageName(): string {
    const preview = this.form.get('preview')?.value;
    return preview?.name || '';
  }

  get contentControls(): FormArray {
    return this.form.get('content') as FormArray;
  }

  addContent() {
    this.contentControls.push(this.fb.control(''));
    this.cdr.detectChanges();
  }

  statusChanged(newStatus: string) {
    this.status = newStatus;
    this.form.get('status')?.setValue(newStatus);
  }

  removeContent(index: number) {
    this.contentControls.removeAt(index);
  }

  hasPixlEditorDescription(): boolean {
    const consumerData = this.form.get('consumerData')?.value;
    return consumerData?.pixl_editor_description === true;
  }

  save(): void {
    if (this.hasPixlEditorDescription()) {
      this.form.get('description')?.setValue(this.descriptionHtml);
    }

    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedModule = Object.assign(
        new ModuleEntity(),
        { id: this.data?.id },
        formValue,
        {
          consumer: formValue.consumer ?
            { type: 'consumers', id: formValue.consumer } :
            null,
          subjects: (formValue.subjects as string[])?.length ?
            (formValue.subjects as string[]).map((id) => ({ id })) :
            [],
          schoolClasses: (formValue.schoolClasses as string[])?.length ?
            (formValue.schoolClasses as string[]).map((id) => ({ id })) :
            [],
          learningUnits: (formValue.learningUnits as string[])?.length ?
            (formValue.learningUnits as string[]).map((id) => ({ id })) :
            [],
        },
      );

      this.dialogRef.close(updatedModule);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackById(index: number, item: LearningUnitEntity) {
    return item.id;
  }
}
